import { useCallback, useEffect, useRef, useState } from 'react'
import websocket from '@/utils/websocket'
import { isError } from '@/helpers/checkQuery'
import type { ResponseWrapper } from '@/api/types/base.types'
import { crmIntegrationChatApi } from '@pages/Admin/CRM/components/integrations/common/api/crm-integration-chat.api'
import type { CrmIntegrationChatListData } from '@pages/Admin/CRM/components/integrations/common/types/crm-integration-chat.types'
import {
  CRM_REALTIME_LIST_REFETCH_DEBOUNCE_MS,
  isIntegrationMessageDeletedWsMessage,
  isIntegrationMessageInsertedWsMessage,
  isIntegrationMessageReadWsMessage,
  isVkMessageInsertedWsMessage,
  isVkMessageReadWsMessage,
  normalizeVkUnreadCount,
} from '@pages/Admin/CRM/utils/crmRealtimeWebSocket'
import debounce from '@/utils/debounce/debounce'

export type WazzupChannelUnreadByExternalId = Record<string, number>

const PAGE_SIZE = 100
const MAX_PAGES = 5

const aggregateChannelUnread = (
  items: CrmIntegrationChatListData['items'] | undefined,
): WazzupChannelUnreadByExternalId => {
  const byChannel: WazzupChannelUnreadByExternalId = {}
  if (!Array.isArray(items)) {
    return byChannel
  }

  items.forEach((item) => {
    const channelId = String(item?.external_channel_id ?? '').trim()
    if (!channelId) {
      return
    }
    const count = normalizeVkUnreadCount(item?.unread_count)
    if (count <= 0) {
      return
    }
    byChannel[channelId] = (byChannel[channelId] ?? 0) + count
  })

  return byChannel
}

/**
 * Unread по каналам Wazzup: сумма unread диалогов (GET crm-integration-chat)
 * сгруппированная по external_channel_id.
 */
export const useWazzupChannelUnreadCounts = (
  integrationId: number | null,
  enabled = true,
): {
  unreadByExternalChannelId: WazzupChannelUnreadByExternalId
  refetch: () => void
} => {
  const [unreadByExternalChannelId, setUnreadByExternalChannelId] =
    useState<WazzupChannelUnreadByExternalId>({})

  const refetch = useCallback(() => {
    if (!enabled || integrationId == null || integrationId <= 0) {
      setUnreadByExternalChannelId({})
      return
    }

    void (async () => {
      try {
        const merged: WazzupChannelUnreadByExternalId = {}
        let page = 1
        let pageCount = 1

        while (page <= pageCount && page <= MAX_PAGES) {
          const res = await crmIntegrationChatApi.getIntegrationChats({
            page,
            perPage: PAGE_SIZE,
            integrationId,
            provider: 'wazzup',
          })
          if (isError(res)) {
            return
          }

          const payload = res.data as ResponseWrapper<CrmIntegrationChatListData>
          const data = payload?.data
          const pageUnread = aggregateChannelUnread(data?.items)
          Object.entries(pageUnread).forEach(([channelId, count]) => {
            merged[channelId] = (merged[channelId] ?? 0) + count
          })

          pageCount = Math.max(1, Number(data?._meta?.pageCount) || 1)
          page += 1
        }

        setUnreadByExternalChannelId(merged)
      } catch {
        /* silent */
      }
    })()
  }, [enabled, integrationId])

  const refetchRef = useRef(refetch)
  refetchRef.current = refetch

  const debouncedRefetchRef = useRef(
    debounce(() => {
      refetchRef.current()
    }, CRM_REALTIME_LIST_REFETCH_DEBOUNCE_MS),
  )

  useEffect(() => {
    refetch()
  }, [refetch])

  useEffect(() => {
    if (!enabled || integrationId == null || integrationId <= 0) {
      return
    }

    websocket.connect()

    const onWs = (message: unknown) => {
      if (
        !isIntegrationMessageInsertedWsMessage(message) &&
        !isIntegrationMessageReadWsMessage(message) &&
        !isIntegrationMessageDeletedWsMessage(message) &&
        !isVkMessageInsertedWsMessage(message) &&
        !isVkMessageReadWsMessage(message)
      ) {
        return
      }

      if (
        isIntegrationMessageInsertedWsMessage(message) ||
        isIntegrationMessageReadWsMessage(message) ||
        isIntegrationMessageDeletedWsMessage(message)
      ) {
        const messageIntegrationId = Number(
          message.data.payload.integration_id,
        )
        if (
          Number.isFinite(messageIntegrationId) &&
          messageIntegrationId > 0 &&
          messageIntegrationId !== integrationId
        ) {
          return
        }
      }

      debouncedRefetchRef.current()
    }

    const unsubInserted = websocket.subscribe(
      'integration_message_inserted',
      onWs,
    )
    const unsubRead = websocket.subscribe('integration_message_read', onWs)
    const unsubDeleted = websocket.subscribe(
      'integration_message_deleted',
      onWs,
    )
    const unsubVkInserted = websocket.subscribe('vk_message_inserted', onWs)
    const unsubVkRead = websocket.subscribe('vk_message_read', onWs)

    return () => {
      unsubInserted?.()
      unsubRead?.()
      unsubDeleted?.()
      unsubVkInserted?.()
      unsubVkRead?.()
    }
  }, [enabled, integrationId])

  return { unreadByExternalChannelId, refetch }
}
